import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Alert,
  AlertDescription,
} from '../ui/alert';
import { Rocket, RefreshCw, Trash2, Download, Mail, Phone, Building2, MapPin, Users, UserCheck, Copy, CheckCircle2, AlertTriangle, ClipboardCopy } from 'lucide-react';
import { toast } from 'sonner';

const PAIN_POINT_LABELS = {
  'lost_leads': 'Lost leads',
  'sub_coordination': 'Sub coordination',
  'follow_ups': 'Follow-ups',
  'estimating_chaos': 'Estimating chaos',
  'other': 'Other',
};

const STATUS_COLORS = {
  new: 'bg-orange-600',
  contacted: 'bg-blue-600',
  invited: 'bg-purple-600',
  approved: 'bg-emerald-600',
  converted: 'bg-green-600',
};

export default function AdminBetaSignups({
  signups,
  statusCounts,
  painPointStats,
  filter,
  setFilter,
  onLoadSignups,
  onUpdateSignup,
  updatingSignup,
  onDeleteSignup,
  onExport,
  onApproveSignup,
}) {
  const [selectedSignup, setSelectedSignup] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null);

  const handleOpenDetail = (signup) => {
    setSelectedSignup({ ...signup });
    setShowDetailDialog(true);
    setApprovalResult(null);
  };

  const handleSaveChanges = async () => {
    if (selectedSignup) {
      await onUpdateSignup(selectedSignup.id, {
        status: selectedSignup.status,
        admin_notes: selectedSignup.admin_notes,
        google_test_user_added: selectedSignup.google_test_user_added || false,
      });
      setShowDetailDialog(false);
    }
  };

  const handleQuickStatus = async (signup, status) => {
    await onUpdateSignup(signup.id, { status });
  };

  const handleApprove = async (signup) => {
    setApproving(true);
    setApprovalResult(null);
    try {
      const result = await onApproveSignup(signup.id);
      setApprovalResult(result);
      if (result.success) {
        toast.success(result.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve signup');
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = () => {
    if (selectedSignup) {
      onDeleteSignup(selectedSignup.id);
      setShowDetailDialog(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleExport = async () => {
    try {
      const data = await onExport();
      if (data && data.signups) {
        const headers = ['email', 'first_name', 'company_name', 'phone', 'num_employees', 'pain_points', 'other_pain_point', 'zip_code', 'status', 'created_at'];
        const csvContent = [
          headers.join(','),
          ...data.signups.map(row => 
            headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',')
          )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `beta_signups_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success('Export downloaded');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  return (
    <>
      <Card className="rounded-sm bg-slate-800 border-slate-700" data-testid="admin-beta-signups">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Rocket className="h-5 w-5 text-orange-400" />
            Beta Testers
            {statusCounts.new > 0 && (
              <Badge className="bg-orange-600 ml-2">{statusCounts.new} new</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-slate-400">
            Manage beta waitlist signups • {statusCounts.total || 0} total
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Status Summary */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className="bg-orange-600">{statusCounts.new || 0} New</Badge>
            <Badge className="bg-blue-600">{statusCounts.contacted || 0} Contacted</Badge>
            <Badge className="bg-purple-600">{statusCounts.invited || 0} Invited</Badge>
            <Badge className="bg-emerald-600">{statusCounts.approved || 0} Approved</Badge>
            <Badge className="bg-green-600">{statusCounts.converted || 0} Converted</Badge>
          </div>

          {/* Pain Point Stats */}
          {painPointStats && Object.keys(painPointStats).length > 0 && (
            <div className="mb-4 p-3 bg-slate-700/50 rounded-sm">
              <p className="text-xs text-slate-400 mb-2">Top Pain Points:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(painPointStats)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([point, count]) => (
                    <Badge key={point} variant="outline" className="border-slate-600 text-slate-300">
                      {PAIN_POINT_LABELS[point] || point}: {count}
                    </Badge>
                  ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <Select 
              value={filter || 'all'} 
              onValueChange={(val) => setFilter(val === 'all' ? '' : val)}
            >
              <SelectTrigger className="w-32 rounded-sm bg-slate-700 border-slate-600 text-white text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExport} className="rounded-sm border-slate-600">
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
              <Button variant="outline" size="sm" onClick={onLoadSignups} className="rounded-sm border-slate-600">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {signups.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No beta signups yet</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {signups.map((signup) => (
                <div
                  key={signup.id}
                  className="p-3 bg-slate-700 rounded-sm hover:bg-slate-600 cursor-pointer"
                  onClick={() => handleOpenDetail(signup)}
                  data-testid={`beta-signup-${signup.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-200">{signup.first_name}</span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-300 text-sm">{signup.company_name}</span>
                      </div>
                      <p className="text-sm text-blue-400">{signup.email}</p>
                      {signup.pain_points && signup.pain_points.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {signup.pain_points.map(pp => (
                            <span key={pp} className="text-xs bg-slate-600 text-slate-300 px-2 py-0.5 rounded">
                              {PAIN_POINT_LABELS[pp] || pp}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        {new Date(signup.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={STATUS_COLORS[signup.status] || 'bg-slate-600'}>
                        {signup.status}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-slate-400 hover:text-slate-200"
                          data-testid={`copy-email-btn-${signup.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(signup.email);
                          }}
                          title="Copy email for Google OAuth test users"
                        >
                          <ClipboardCopy className="h-3 w-3" />
                        </Button>
                        {signup.status !== 'approved' && signup.status !== 'converted' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
                            data-testid={`approve-btn-${signup.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(signup);
                            }}
                            disabled={approving}
                          >
                            <UserCheck className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Signup Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={(open) => { setShowDetailDialog(open); if (!open) setApprovalResult(null); }}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-orange-400" />
              Beta Signup Details
            </DialogTitle>
            <DialogDescription className="sr-only">Review and manage beta signup details</DialogDescription>
          </DialogHeader>
          {selectedSignup && (
            <div className="space-y-4">
              {/* Approval Result Banner */}
              {approvalResult && approvalResult.success && (
                <div className="p-3 bg-emerald-900/30 border border-emerald-700 rounded-sm" data-testid="approval-result">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    <span className="font-medium text-emerald-300">Account Created</span>
                  </div>
                  <p className="text-sm text-emerald-200 mb-2">{approvalResult.message}</p>
                  {approvalResult.temp_password && (
                    <div className="bg-slate-900 p-2 rounded mt-2">
                      <p className="text-xs text-slate-400 mb-1">Temp password (email failed to send — share manually):</p>
                      <div className="flex items-center gap-2">
                        <code className="text-sm text-orange-300 font-mono">{approvalResult.temp_password}</code>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(approvalResult.temp_password)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {approvalResult.email_sent && (
                    <p className="text-xs text-emerald-400 mt-1">Welcome email sent with login credentials.</p>
                  )}
                  {/* Google OAuth reminder */}
                  <Alert className="mt-3 bg-amber-900/30 border-amber-700">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <AlertDescription className="text-amber-200 text-xs">
                      Don't forget to add <strong>{selectedSignup.email}</strong> as a test user in 
                      Google Cloud Console (OAuth consent screen) so they can use "Sign in with Google".
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Google OAuth Test User Checklist */}
              <div className="p-3 bg-slate-700/50 rounded-sm" data-testid="google-oauth-checklist">
                <p className="text-xs text-slate-400 mb-2 font-medium">Google OAuth Checklist</p>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`google-added-${selectedSignup.id}`}
                    checked={selectedSignup.google_test_user_added || false}
                    onCheckedChange={(checked) => setSelectedSignup({ ...selectedSignup, google_test_user_added: checked })}
                    data-testid="google-oauth-checkbox"
                  />
                  <label htmlFor={`google-added-${selectedSignup.id}`} className="text-sm text-slate-300 cursor-pointer">
                    Added to Google Cloud Console test users
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-xs text-blue-400 hover:text-blue-300 ml-auto"
                    onClick={() => copyToClipboard(selectedSignup.email)}
                    data-testid="copy-email-detail-btn"
                  >
                    <ClipboardCopy className="h-3 w-3 mr-1" />
                    Copy Email
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-xs">Name</p>
                  <p className="font-medium">{selectedSignup.first_name}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Status</p>
                  <Select
                    value={selectedSignup.status}
                    onValueChange={(val) => setSelectedSignup({ ...selectedSignup, status: val })}
                  >
                    <SelectTrigger className="w-full mt-1 rounded-sm bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="invited">Invited</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <a href={`mailto:${selectedSignup.email}`} className="text-blue-400 hover:underline">
                    {selectedSignup.email}
                  </a>
                </div>
                {selectedSignup.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <a href={`tel:${selectedSignup.phone}`} className="text-slate-300">
                      {selectedSignup.phone}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-300">{selectedSignup.company_name}</span>
                </div>
                {selectedSignup.num_employees && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-300">{selectedSignup.num_employees} employees</span>
                  </div>
                )}
                {selectedSignup.zip_code && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-300">{selectedSignup.zip_code}</span>
                  </div>
                )}
              </div>
              
              {selectedSignup.pain_points && selectedSignup.pain_points.length > 0 && (
                <div>
                  <p className="text-slate-400 text-xs mb-2">Pain Points</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedSignup.pain_points.map(pp => (
                      <Badge key={pp} variant="outline" className="border-orange-500/50 text-orange-400">
                        {PAIN_POINT_LABELS[pp] || pp}
                      </Badge>
                    ))}
                  </div>
                  {selectedSignup.other_pain_point && (
                    <p className="text-sm text-slate-300 mt-2 italic">
                      Other: {selectedSignup.other_pain_point}
                    </p>
                  )}
                </div>
              )}
              
              <div>
                <p className="text-slate-400 text-xs mb-1">Signed up</p>
                <p className="text-sm">{new Date(selectedSignup.created_at).toLocaleString()}</p>
              </div>

              {selectedSignup.approved_at && (
                <div>
                  <p className="text-slate-400 text-xs mb-1">Approved</p>
                  <p className="text-sm text-emerald-400">{new Date(selectedSignup.approved_at).toLocaleString()}</p>
                </div>
              )}
              
              <div>
                <Label className="text-slate-400 text-xs">Admin Notes</Label>
                <Textarea
                  value={selectedSignup.admin_notes || ''}
                  onChange={(e) => setSelectedSignup({ ...selectedSignup, admin_notes: e.target.value })}
                  placeholder="Add notes..."
                  className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white"
                  rows={2}
                />
              </div>
              
              <div className="flex justify-between pt-2">
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    className="rounded-sm"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                  {selectedSignup.status !== 'approved' && selectedSignup.status !== 'converted' && (
                    <Button
                      size="sm"
                      onClick={() => handleApprove(selectedSignup)}
                      disabled={approving}
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                      data-testid="approve-detail-btn"
                    >
                      <UserCheck className="h-4 w-4 mr-2" />
                      {approving ? 'Approving...' : 'Approve & Create Account'}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-sm border-slate-600">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={updatingSignup}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    {updatingSignup ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
